import type { PublicTableResponse } from '@/lib/api/public';
import { defaultRecConfig, mergeRecConfig, type RecConfig } from '@/config/recs';

export type RecommendationSource = 'personalized' | 'history' | 'popular' | 'signals';

export interface RecommendationBreakdown {
  score: number;
  reasons: string[];
}

export interface RecommendationTasteSnapshot {
  tags: string[];
  cuisines: string[];
  heat: string | null;
  tagScores: Record<string, number>;
  cuisineAffinity: Record<string, number>;
}

export interface RecommendationState<TItem extends RecommendationItemLike> {
  items: TItem[];
  source: RecommendationSource;
  breakdown: Record<string, RecommendationBreakdown>;
  consentPersonalization: boolean;
  tasteSnapshot: RecommendationTasteSnapshot | null;
  holdout: boolean;
  modelVersion?: string;
  guestProfileId?: string | null;
}

export interface RecommendationItemLike {
  id: string;
  tags?: string[];
  cuisines?: string[];
  tasteHeat?: string | null;
  popular?: boolean;
  categoryId?: string | null;
  marginCents?: number | null;
  prepMinutes?: number | null;
  station?: string | null;
  isNew?: boolean | null;
}

export type RecommendationItemFeatureVector = {
  tagSet: Set<string>;
  cuisineSet: Set<string>;
  heat: string | null;
};

export interface RecommendationMetricsSnapshot {
  totalImpressions?: number;
  itemImpressions?: Record<string, number>;
}

export interface RecommendationStationPenaltyEvent {
  station: string;
  load: number;
  normalizedLoad: number;
  penalty: number;
  baseScore: number;
  maxShare: number;
  threshold: number;
}

export interface RecommendationContextFeatures {
  stationLoad?: Record<string, number>;
  stationPenaltyObserver?: (event: RecommendationStationPenaltyEvent) => void;
}

const heatRank: Record<string, number> = {
  mild: 1,
  light: 1,
  medium: 2,
  'medium-hot': 3,
  medium_hot: 3,
  spicy: 4,
  hot: 4,
  'extra-hot': 5,
  extra_hot: 5,
};

const getHeatRank = (heat: string | null | undefined): number => {
  if (!heat) return 0;
  return heatRank[heat] ?? 0;
};

export const normaliseTasteString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
};

export const toUniqueStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const set = new Set<string>();
  for (const entry of value) {
    const normalized = normaliseTasteString(entry);
    if (normalized) {
      set.add(normalized);
    }
  }
  return Array.from(set);
};

export const coerceNumberRecord = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const input = value as Record<string, unknown>;
  const result: Record<string, number> = {};
  for (const [rawKey, rawValue] of Object.entries(input)) {
    const key = normaliseTasteString(rawKey);
    if (!key) continue;
    const numeric =
      typeof rawValue === 'number'
        ? rawValue
        : typeof rawValue === 'string'
          ? Number.parseFloat(rawValue)
          : null;
    if (numeric === null || Number.isNaN(numeric)) {
      continue;
    }
    result[key] = Number(Number(numeric).toFixed(3));
  }
  return result;
};

export const mergeNumberRecords = (
  ...records: Array<Record<string, number> | undefined | null>
): Record<string, number> => {
  const merged: Record<string, number> = {};
  for (const record of records) {
    if (!record) continue;
    for (const [key, value] of Object.entries(record)) {
      if (!Number.isFinite(value)) continue;
      merged[key] = Number(value.toFixed(3));
    }
  }
  return merged;
};

const DEFAULT_LIMIT = 6;
const DEFAULT_MODEL_VERSION = 'rec-engine/v2';
const SIMILARITY_SCALE = 100;
const DEFAULT_STATION_PENALTY_THRESHOLD = 0.6;
const DEFAULT_STATION_PENALTY_MAX_SHARE = 0.2;

const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
};

const log1pSafe = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.log(1 + value);
};

export const explorationBonus = (
  totalImpressions: number,
  itemImpressions: number,
  alpha: number,
): number => {
  if (!Number.isFinite(totalImpressions) || totalImpressions <= 0 || alpha <= 0) {
    return 0;
  }
  const numerator = Math.log(totalImpressions + 1);
  if (numerator <= 0) {
    return 0;
  }
  return alpha * Math.sqrt(numerator / (Math.max(0, itemImpressions) + 1));
};

export const profitTerm = (marginCents?: number | null, beta = 0.2): number => {
  if (!marginCents || marginCents <= 0 || beta <= 0) {
    return 0;
  }
  return beta * log1pSafe(marginCents);
};

type StationPenaltyOptions = {
  baseScore?: number;
  threshold?: number;
  maxShare?: number;
  observer?: (event: RecommendationStationPenaltyEvent) => void;
};

export const stationPenalty = (
  station: string | null | undefined,
  stationLoad: Record<string, number> | undefined,
  gamma = 0.15,
  options?: StationPenaltyOptions,
): number => {
  if (!station || !stationLoad || gamma <= 0) {
    return 0;
  }
  const normalizedStation = station.trim();
  if (!normalizedStation) {
    return 0;
  }

  const resolvedLoad =
    stationLoad[normalizedStation] ??
    stationLoad[normalizedStation.toLowerCase?.() ?? normalizedStation] ??
    stationLoad[normalizedStation.toUpperCase?.() ?? normalizedStation];
  if (typeof resolvedLoad !== 'number' || Number.isNaN(resolvedLoad)) {
    return 0;
  }

  const threshold = typeof options?.threshold === 'number' ? options.threshold : DEFAULT_STATION_PENALTY_THRESHOLD;
  if (resolvedLoad < threshold) {
    return 0;
  }

  const baseScore = Math.max(0, options?.baseScore ?? 0);
  if (baseScore <= 0) {
    return 0;
  }

  const normalizedLoad = clamp01((resolvedLoad - threshold) / Math.max(1e-6, 1 - threshold));
  if (normalizedLoad <= 0) {
    return 0;
  }

  const maxShare = typeof options?.maxShare === 'number' ? Math.max(0, Math.min(1, options.maxShare)) : DEFAULT_STATION_PENALTY_MAX_SHARE;
  if (maxShare <= 0) {
    return 0;
  }

  const rawPenaltyMagnitude = gamma * SIMILARITY_SCALE * normalizedLoad;
  if (rawPenaltyMagnitude <= 0) {
    return 0;
  }

  const cappedMagnitude = Math.min(rawPenaltyMagnitude, baseScore * maxShare);
  if (cappedMagnitude <= 0) {
    return 0;
  }

  const appliedPenalty = -cappedMagnitude;

  if (typeof options?.observer === 'function') {
    options.observer({
      station: normalizedStation,
      load: resolvedLoad,
      normalizedLoad,
      penalty: appliedPenalty,
      baseScore,
      maxShare,
      threshold,
    });
  }

  return appliedPenalty;
};

const normalizeCategoryId = (value: string | null | undefined): string => {
  if (typeof value !== 'string') {
    return 'uncategorized';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return 'uncategorized';
  }
  return trimmed.toLowerCase();
};

const similarityScore = (a: RecommendationItemLike, b: RecommendationItemLike): number => {
  const tagsA = new Set([...(a.tags ?? []), ...(a.cuisines ?? [])].map((tag) => tag?.toLowerCase()).filter(Boolean));
  const tagsB = new Set([...(b.tags ?? []), ...(b.cuisines ?? [])].map((tag) => tag?.toLowerCase()).filter(Boolean));
  if (!tagsA.size && !tagsB.size) {
    return 0;
  }
  let overlap = 0;
  for (const tag of tagsA) {
    if (tag && tagsB.has(tag)) {
      overlap += 1;
    }
  }
  const union = new Set<string>([...tagsA, ...tagsB]).size || 1;
  return overlap / union;
};

export const rerankDiverse = <TItem extends RecommendationItemLike>(
  candidates: Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }>,
  limit: number,
  lambda: number,
): Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }> => {
  if (lambda <= 0 || candidates.length <= 1) {
    if (candidates.length > limit) {
      candidates.length = limit;
    }
    return candidates;
  }
  const available = candidates;
  const selected: Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }> = [];
  while (selected.length < limit && available.length) {
    let pick = 0;
    let pickScore = -Infinity;
    for (let i = 0; i < available.length; i += 1) {
      const candidate = available[i];
      const baseScore = candidate.score;
      if (!Number.isFinite(baseScore)) continue;
      let maxSim = 0;
      if (selected.length > 0) {
        for (const prior of selected) {
          const sim = similarityScore(candidate.item, prior.item);
          if (sim > maxSim) {
            maxSim = sim;
          }
        }
      }
      const penaltyScale = Math.max(Math.abs(baseScore), SIMILARITY_SCALE);
      const mmrScore = baseScore - lambda * penaltyScale * maxSim;
      if (mmrScore > pickScore) {
        pick = i;
        pickScore = mmrScore;
      }
    }
    const [chosen] = available.splice(pick, 1);
    if (chosen) {
      selected.push(chosen);
    }
  }
  return selected;
};

const enforceCategoryDiversityRules = <TItem extends RecommendationItemLike>(
  orderedCandidates: Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }>,
  candidatePool: Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }>,
  limit: number,
  config: RecConfig['categoryDiversity'],
): Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }> => {
  if (!config.enabled || limit <= 1 || candidatePool.length === 0) {
    return orderedCandidates.slice(0, limit);
  }

  const maxPerCategory = Math.max(1, config.maxPerCategory);
  const prioritizedCategories = new Set(config.prioritizedCategories.map((entry) => entry.toLowerCase()));
  const categoryTotals = new Map<string, number>();
  for (const entry of candidatePool) {
    const category = normalizeCategoryId(entry.item.categoryId);
    categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + 1);
  }

  const hasAlternativeCategories = (
    currentCategory: string,
    categoryCounts: Map<string, number>,
  ): boolean => {
    for (const [category, total] of categoryTotals.entries()) {
      if (category === currentCategory) continue;
      const used = categoryCounts.get(category) ?? 0;
      if (total > used) {
        return true;
      }
    }
    return false;
  };

  const result: typeof orderedCandidates = [];
  const categoryCounts = new Map<string, number>();
  const selectedIds = new Set<string>();
  const skipped: typeof orderedCandidates = [];

  const addEntry = (entry: (typeof orderedCandidates)[number], insertIndex?: number): boolean => {
    if (selectedIds.has(entry.item.id)) {
      return false;
    }
    const category = normalizeCategoryId(entry.item.categoryId);
    if (typeof insertIndex === 'number' && insertIndex >= 0 && insertIndex < result.length) {
      result.splice(insertIndex, 0, entry);
    } else {
      result.push(entry);
    }
    selectedIds.add(entry.item.id);
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
    return true;
  };

  const removeEntryAt = (index: number): void => {
    const [removed] = result.splice(index, 1);
    if (removed) {
      const category = normalizeCategoryId(removed.item.categoryId);
      selectedIds.delete(removed.item.id);
      categoryCounts.set(category, Math.max(0, (categoryCounts.get(category) ?? 1) - 1));
    }
  };

  for (const entry of orderedCandidates) {
    if (result.length >= limit) break;
    const category = normalizeCategoryId(entry.item.categoryId);
    const currentCount = categoryCounts.get(category) ?? 0;
    const totalForCategory = categoryTotals.get(category) ?? 0;
    if (
      currentCount >= maxPerCategory &&
      totalForCategory > currentCount &&
      hasAlternativeCategories(category, categoryCounts)
    ) {
      skipped.push(entry);
      continue;
    }
    addEntry(entry);
  }

  const fillFromCandidates = (candidates: typeof orderedCandidates): void => {
    for (const entry of candidates) {
      if (result.length >= limit) break;
      addEntry(entry);
    }
  };

  if (result.length < limit) {
    fillFromCandidates(skipped);
  }
  if (result.length < limit) {
    fillFromCandidates(candidatePool);
  }

  const findReplacementIndex = (disallowCategories: Set<string>): number => {
    let maxCount = 0;
    let replacementIndex = -1;
    for (let i = 0; i < result.length; i += 1) {
      const entry = result[i];
      const category = normalizeCategoryId(entry.item.categoryId);
      if (disallowCategories.has(category)) continue;
      const count = categoryCounts.get(category) ?? 0;
      if (count <= 1) continue;
      if (count > maxCount) {
        maxCount = count;
        replacementIndex = i;
      }
    }
    return replacementIndex;
  };

  const includeCategoryIfPossible = (targetCategory: string): boolean => {
    const normalizedTarget = targetCategory.toLowerCase();
    if (result.some((entry) => normalizeCategoryId(entry.item.categoryId) === normalizedTarget)) {
      return true;
    }
    const replacement = candidatePool.find(
      (entry) =>
        normalizeCategoryId(entry.item.categoryId) === normalizedTarget && !selectedIds.has(entry.item.id),
    );
    if (!replacement) {
      return false;
    }
    const replacementIndex = findReplacementIndex(new Set([normalizedTarget, ...prioritizedCategories]));
    if (replacementIndex === -1) {
      if (result.length >= limit) {
        return false;
      }
      return addEntry(replacement);
    }
    removeEntryAt(replacementIndex);
    result.splice(replacementIndex, 0, replacement);
    const category = normalizeCategoryId(replacement.item.categoryId);
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
    selectedIds.add(replacement.item.id);
    return true;
  };

  for (const category of prioritizedCategories) {
    includeCategoryIfPossible(category);
  }

  const minUnique = Math.max(0, config.minUniqueCategories);
  if (minUnique > 0) {
    const uniqueCategories = new Set(result.map((entry) => normalizeCategoryId(entry.item.categoryId)));
    const availableCategories = Array.from(categoryTotals.keys()).filter(
      (category) => !uniqueCategories.has(category),
    );
    for (const category of availableCategories) {
      if (uniqueCategories.size >= minUnique) {
        break;
      }
      const added = includeCategoryIfPossible(category);
      if (added) {
        uniqueCategories.add(category.toLowerCase());
      }
    }
  }

  return result.slice(0, limit);
};

type RecommendationHistoryFlags = {
  favorite: boolean;
  recent: boolean;
};

const enforceHistoryCaps = <TItem extends RecommendationItemLike>(
  entries: Array<{ item: TItem; score: number; reasons: Set<string>; flags: RecommendationHistoryFlags }>,
  caps: { maxFavorites: number; maxRecent: number },
  limit: number,
) => {
  const selected: typeof entries = [];
  let favoriteCount = 0;
  let recentCount = 0;
  for (const entry of entries) {
    const isFavorite = entry.flags.favorite;
    const isRecent = entry.flags.recent;
    if (isFavorite && favoriteCount >= caps.maxFavorites) {
      continue;
    }
    if (isRecent && recentCount >= caps.maxRecent) {
      continue;
    }
    selected.push(entry);
    if (isFavorite) favoriteCount += 1;
    if (isRecent) recentCount += 1;
    if (selected.length >= limit * 2) {
      break;
    }
  }
  return selected.length ? selected : entries.slice(0, Math.min(entries.length, limit * 2));
};

export const extractSnapshotItemIds = (snapshot: unknown): string[] => {
  if (!snapshot || typeof snapshot !== 'object') {
    return [];
  }
  const record = snapshot as Record<string, unknown>;
  const itemsRaw = record.items;
  if (!Array.isArray(itemsRaw)) {
    return [];
  }
  const ids = new Set<string>();
  for (const entry of itemsRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const candidate =
      typeof row.itemId === 'string'
        ? row.itemId
        : typeof row.menuItemId === 'string'
          ? row.menuItemId
          : typeof row.id === 'string'
            ? row.id
            : null;
    if (!candidate) continue;
    const normalized = candidate.trim();
    if (normalized.length > 0) {
      ids.add(normalized);
    }
  }
  return Array.from(ids);
};

export const resolvePersonalizationConsent = (consent: unknown): boolean => {
  if (!consent || typeof consent !== 'object') {
    return true;
  }
  const record = consent as { personalization?: unknown };
  if (typeof record.personalization === 'boolean') {
    return record.personalization;
  }
  return true;
};

const snapshotKeys = [
  'taste_profile',
  'tasteProfile',
  'taste',
] as const;

export const buildGuestTasteSnapshot = (
  guest: Partial<PublicTableResponse['guest_profile']> | Record<string, unknown> | null | undefined,
): RecommendationTasteSnapshot | null => {
  if (!guest) {
    return null;
  }
  const guestRecord = guest as Record<string, unknown>;
  let tasteSource: Record<string, unknown> = {};
  for (const key of snapshotKeys) {
    const value = guestRecord[key];
    if (value && typeof value === 'object') {
      tasteSource = value as Record<string, unknown>;
      break;
    }
  }
  const tags = new Set<string>();
  const cuisines = new Set<string>();

  const addTags = (value: unknown) => {
    for (const tag of toUniqueStringList(value)) {
      tags.add(tag);
    }
  };
  const addCuisines = (value: unknown) => {
    for (const cuisine of toUniqueStringList(value)) {
      cuisines.add(cuisine);
    }
  };

  addTags(tasteSource.tags);
  addTags((tasteSource as { tag_list?: unknown }).tag_list);
  addTags(guestRecord.taste_tags);
  addTags(guestRecord.tasteTags);
  addTags(guestRecord.taste_cuisines);
  addTags(guestRecord.tasteCuisines);
  addTags((tasteSource as { cuisines?: unknown }).cuisines);
  addTags((tasteSource as { cuisine_list?: unknown }).cuisine_list);

  addCuisines(tasteSource.cuisines);
  addCuisines(guestRecord.taste_cuisines);
  addCuisines(guestRecord.tasteCuisines);

  const tagScores = mergeNumberRecords(
    coerceNumberRecord((tasteSource as { tagScores?: unknown }).tagScores),
    coerceNumberRecord((tasteSource as { tag_scores?: unknown }).tag_scores),
    coerceNumberRecord(guestRecord.taste_tag_scores as Record<string, number> | undefined),
    coerceNumberRecord(guestRecord.tasteTagScores as Record<string, number> | undefined),
  );

  const cuisineAffinity = mergeNumberRecords(
    coerceNumberRecord((tasteSource as { cuisineAffinity?: unknown }).cuisineAffinity),
    coerceNumberRecord((tasteSource as { cuisine_affinity?: unknown }).cuisine_affinity),
    coerceNumberRecord(guestRecord.taste_cuisine_affinity as Record<string, number> | undefined),
    coerceNumberRecord(guestRecord.tasteCuisineAffinity as Record<string, number> | undefined),
  );

  for (const key of Object.keys(tagScores)) {
    tags.add(key);
  }
  for (const key of Object.keys(cuisineAffinity)) {
    cuisines.add(key);
  }

  const heatCandidate =
    normaliseTasteString((tasteSource as { heat?: unknown }).heat) ??
    normaliseTasteString(guestRecord.taste_heat) ??
    normaliseTasteString(guestRecord.tasteHeat);

  if (tags.size === 0 && cuisines.size === 0 && !heatCandidate) {
    return null;
  }

  return {
    tags: Array.from(tags),
    cuisines: Array.from(cuisines),
    heat: heatCandidate ?? null,
    tagScores,
    cuisineAffinity,
  };
};

export const computePopularFallback = <TItem extends RecommendationItemLike>(
  items: TItem[],
  limit = DEFAULT_LIMIT,
): { items: TItem[]; breakdown: Record<string, RecommendationBreakdown> } => {
  const sorted = items
    .slice()
    .sort(
      (a, b) =>
        Number(Boolean(b.popular)) - Number(Boolean(a.popular)) ||
        (a.tasteHeat ? 0 : 1) - (b.tasteHeat ? 0 : 1) ||
        a.id.localeCompare(b.id),
    );
  const top = sorted.slice(0, limit);
  const breakdown: Record<string, RecommendationBreakdown> = {};
  for (const item of top) {
    breakdown[item.id] = {
      score: item.popular ? 12 : 8,
      reasons: [item.popular ? 'popular' : 'fallback'],
    };
  }
  return { items: top, breakdown };
};

export function buildRecommendationState<TItem extends RecommendationItemLike>({
  items,
  favorites,
  recentItemIds,
  tasteSnapshot,
  consentPersonalization,
  engagementSignals,
  limit = DEFAULT_LIMIT,
  metrics,
  context,
  config,
  guestProfileId = null,
  modelVersion,
  suppressedItemIds,
  holdout = false,
  featureVectors,
}: {
  items: TItem[];
  favorites: Set<string>;
  recentItemIds: Set<string>;
  tasteSnapshot: RecommendationTasteSnapshot | null;
  consentPersonalization: boolean;
  engagementSignals?: Map<string, number> | null;
  limit?: number;
  metrics?: RecommendationMetricsSnapshot | null;
  context?: RecommendationContextFeatures | null;
  config?: Partial<RecConfig> | null;
  guestProfileId?: string | null;
  modelVersion?: string | null;
  suppressedItemIds?: Set<string>;
  holdout?: boolean;
  featureVectors?: Map<string, RecommendationItemFeatureVector> | null;
}): RecommendationState<TItem> {
  const resolvedLimit = Math.max(1, limit);
  const resolvedModelVersion = modelVersion ?? DEFAULT_MODEL_VERSION;

  const allowPersonalizedSignals = consentPersonalization && !holdout;
  const effectiveConsent = allowPersonalizedSignals;

  if (items.length === 0) {
    return {
      items: [],
      source: 'popular',
      breakdown: {},
      consentPersonalization: effectiveConsent,
      tasteSnapshot: null,
      holdout,
      modelVersion: resolvedModelVersion,
      guestProfileId,
    };
  }

  const effectiveConfig = config ? mergeRecConfig(defaultRecConfig, config) : mergeRecConfig(defaultRecConfig);
  const tasteTags = new Set(allowPersonalizedSignals ? tasteSnapshot?.tags ?? [] : []);
  const tasteCuisines = new Set(allowPersonalizedSignals ? tasteSnapshot?.cuisines ?? [] : []);
  const tagWeights = new Map<string, number>(
    allowPersonalizedSignals
      ? Object.entries(tasteSnapshot?.tagScores ?? {}).map(([key, value]) => [key, Math.max(0, value)])
      : [],
  );
  const cuisineWeights = new Map<string, number>(
    allowPersonalizedSignals
      ? Object.entries(tasteSnapshot?.cuisineAffinity ?? {}).map(([key, value]) => [key, Math.max(0, value)])
      : [],
  );

  if (tagWeights.size === 0) {
    for (const tag of tasteTags) {
      tagWeights.set(tag, 1);
    }
  }
  if (cuisineWeights.size === 0) {
    for (const cuisine of tasteCuisines) {
      cuisineWeights.set(cuisine, 1);
    }
  }

  const heatPreference = allowPersonalizedSignals ? tasteSnapshot?.heat ?? null : null;
  const hasTasteSignals =
    tasteTags.size > 0 ||
    tasteCuisines.size > 0 ||
    tagWeights.size > 0 ||
    cuisineWeights.size > 0 ||
    Boolean(heatPreference);
  const activeFavorites = allowPersonalizedSignals ? favorites : new Set<string>();
  const activeRecents = allowPersonalizedSignals ? recentItemIds : new Set<string>();
  const hasHistorySignals = activeFavorites.size > 0 || activeRecents.size > 0;
  const hasEngagementSignals = Boolean(allowPersonalizedSignals && engagementSignals && engagementSignals.size > 0);

  const totalImpressions = metrics?.totalImpressions ?? 0;
  const itemImpressions = metrics?.itemImpressions ?? {};
  const stationLoad = context?.stationLoad;
  const suppressed = suppressedItemIds ?? new Set<string>();

  const providedVectorMap = featureVectors ?? null;
  const localVectorCache = new Map<string, RecommendationItemFeatureVector>();
  const getItemVectors = (item: TItem): RecommendationItemFeatureVector => {
    const cached = localVectorCache.get(item.id);
    if (cached) {
      return cached;
    }
    if (providedVectorMap?.has(item.id)) {
      const provided = providedVectorMap.get(item.id)!;
      localVectorCache.set(item.id, provided);
      return provided;
    }
    const computed: RecommendationItemFeatureVector = {
      tagSet: new Set<string>(
        Array.isArray(item.tags)
          ? item.tags.map((tag) => normaliseTasteString(tag) ?? '').filter(Boolean)
          : [],
      ),
      cuisineSet: new Set<string>(
        Array.isArray(item.cuisines)
          ? item.cuisines.map((cuisine) => normaliseTasteString(cuisine) ?? '').filter(Boolean)
          : [],
      ),
      heat: normaliseTasteString(item.tasteHeat),
    };
    localVectorCache.set(item.id, computed);
    return computed;
  };

  const needsTagMatch = allowPersonalizedSignals && tasteTags.size > 0;
  const needsCuisineMatch = allowPersonalizedSignals && tasteCuisines.size > 0;
  const needsHeatMatch = allowPersonalizedSignals && Boolean(heatPreference);

  const scored: Array<{
    item: TItem;
    score: number;
    reasons: Set<string>;
    flags: RecommendationHistoryFlags;
  }> = [];

  for (const item of items) {
    let score = 0;
    const reasons = new Set<string>();
    const isFavorite = activeFavorites.has(item.id);
    const isRecent = activeRecents.has(item.id);

    if (suppressed.has(item.id)) {
      continue;
    }

    if (allowPersonalizedSignals && isFavorite) {
      score += 120;
      reasons.add('favorite');
    }
    if (allowPersonalizedSignals && isRecent) {
      score += 70;
      reasons.add('recent');
    }

    let vectors: RecommendationItemFeatureVector | null = null;
    const ensureVectors = (): RecommendationItemFeatureVector => {
      if (!vectors) {
        vectors = getItemVectors(item);
      }
      return vectors;
    };

    const matchedTags: string[] = [];
    if (needsTagMatch) {
      const tagSet = ensureVectors().tagSet;
      if (tagSet.size > 0) {
        let tagScore = 0;
        for (const tag of tagSet) {
          if (!tag || !tasteTags.has(tag)) continue;
          matchedTags.push(tag);
          const weight = tagWeights.get(tag) ?? 1;
          tagScore += 28 * (0.6 + Math.min(1.4, weight));
        }
        if (tagScore > 0) {
          score += tagScore;
          reasons.add(`tag:${matchedTags[0]}`);
          if (matchedTags.length > 1) {
            reasons.add('tag:multi');
          }
        }
      }
    }

    const matchedCuisines: string[] = [];
    if (needsCuisineMatch) {
      const cuisineSet = ensureVectors().cuisineSet;
      if (cuisineSet.size > 0) {
        let cuisineScore = 0;
        for (const cuisine of cuisineSet) {
          if (!cuisine || !tasteCuisines.has(cuisine)) continue;
          matchedCuisines.push(cuisine);
          const weight = cuisineWeights.get(cuisine) ?? 1;
          cuisineScore += 22 * (0.6 + Math.min(1.4, weight));
        }
        if (cuisineScore > 0) {
          score += cuisineScore;
          reasons.add(`cuisine:${matchedCuisines[0]}`);
        }
      }
    }

    if (needsHeatMatch) {
      const itemHeat = ensureVectors().heat;
      if (itemHeat && heatPreference) {
        const prefRank = getHeatRank(heatPreference);
        const itemRank = getHeatRank(itemHeat);
        if (prefRank > 0 && itemRank > 0) {
          const delta = Math.abs(prefRank - itemRank);
          if (delta === 0) {
            score += 16;
            reasons.add('heat:match');
          } else if (delta === 1) {
            score += 8;
            reasons.add('heat:close');
          }
        }
      }
    }

    if (effectiveConfig.enabled.exploration) {
      const bonus = explorationBonus(totalImpressions, itemImpressions[item.id] ?? 0, effectiveConfig.alphaExplore);
      if (bonus > 0.1) {
        score += bonus;
        reasons.add('context:explore');
      }
    }

    if (effectiveConfig.enabled.margin) {
      const profit = profitTerm(item.marginCents, effectiveConfig.betaMargin);
      if (profit > 0.1) {
        score += profit;
        reasons.add('value:margin');
      }
    }

    if (allowPersonalizedSignals && !isFavorite && !isRecent && score > 0) {
      score += 12;
      reasons.add('novel');
    }

    if (item.isNew) {
      reasons.add('tag:new');
      const impressionsForItem = itemImpressions[item.id] ?? 0;
      const horizon = Math.max(1, effectiveConfig.newItemHorizonImpressions);
      const maxBoost = Math.max(0, effectiveConfig.newItemMaxBoost);
      if (maxBoost > 0 && impressionsForItem >= 0 && Number.isFinite(impressionsForItem)) {
        const decay = Math.exp(-impressionsForItem / horizon);
        const newBoost = maxBoost * decay;
        if (newBoost > 0.1) {
          score += newBoost;
          reasons.add('new:explore');
        }
      }
    }

    if (item.popular) {
      score += 6;
      reasons.add('popular');
    }

    if (allowPersonalizedSignals && engagementSignals && engagementSignals.has(item.id)) {
      const rawSignal = engagementSignals.get(item.id) ?? 0;
      if (rawSignal !== 0) {
        const signalBoost = Math.sign(rawSignal) * Math.min(22, Math.log1p(Math.abs(rawSignal)) * 6);
        score += signalBoost;
        reasons.add(rawSignal > 0 ? 'signal:engagement' : 'signal:cooldown');
      }
    }

    if (effectiveConfig.enabled.station && stationLoad) {
      const baseScoreForPenalty = score;
      const penalty = stationPenalty(item.station ?? null, stationLoad, effectiveConfig.gammaStation, {
        baseScore: baseScoreForPenalty,
        threshold: DEFAULT_STATION_PENALTY_THRESHOLD,
        maxShare: DEFAULT_STATION_PENALTY_MAX_SHARE,
        observer: context?.stationPenaltyObserver,
      });
      if (penalty !== 0) {
        score += penalty;
      }
    }

    if (score > 0) {
      scored.push({ item, score, reasons, flags: { favorite: isFavorite, recent: isRecent } });
    }
  }

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      Number(Boolean(b.item.popular)) - Number(Boolean(a.item.popular)) ||
      a.item.id.localeCompare(b.item.id),
  );

  if (scored.length === 0) {
    const fallback = computePopularFallback(items, resolvedLimit);
    return {
      items: fallback.items,
      source: hasEngagementSignals ? 'signals' : 'popular',
      breakdown: fallback.breakdown,
      consentPersonalization: effectiveConsent,
      tasteSnapshot: hasTasteSignals ? tasteSnapshot : null,
      holdout,
      modelVersion: resolvedModelVersion,
      guestProfileId,
    };
  }

  const capped = enforceHistoryCaps(scored, effectiveConfig.caps, resolvedLimit);
  const candidatePool = capped;
  if (candidatePool.length > resolvedLimit * 2) {
    candidatePool.length = resolvedLimit * 2;
  }
  let topEntries: typeof candidatePool;
  if (effectiveConfig.enabled.diversity) {
    topEntries = rerankDiverse(candidatePool, resolvedLimit, effectiveConfig.lambdaDiversity);
  } else {
    if (candidatePool.length > resolvedLimit) {
      candidatePool.length = resolvedLimit;
    }
    topEntries = candidatePool;
  }

  if (effectiveConfig.categoryDiversity.enabled) {
    topEntries = enforceCategoryDiversityRules(
      topEntries,
      candidatePool,
      resolvedLimit,
      effectiveConfig.categoryDiversity,
    );
  }

  if (topEntries.length === 0) {
    const fallback = computePopularFallback(items, resolvedLimit);
    return {
      items: fallback.items,
      source: hasEngagementSignals ? 'signals' : 'popular',
      breakdown: fallback.breakdown,
      consentPersonalization: effectiveConsent,
      tasteSnapshot: hasTasteSignals ? tasteSnapshot : null,
      holdout,
      modelVersion: resolvedModelVersion,
      guestProfileId,
    };
  }

  if (
    consentPersonalization &&
    topEntries.every((entry) => entry.flags.favorite || entry.flags.recent)
  ) {
    const novel = scored.find((entry) => !entry.flags.favorite && !entry.flags.recent);
    if (novel) {
      topEntries[topEntries.length - 1] = novel;
      topEntries.sort(
        (a, b) =>
          b.score - a.score ||
          Number(Boolean(b.item.popular)) - Number(Boolean(a.item.popular)) ||
          a.item.id.localeCompare(b.item.id),
      );
    }
  }

  const breakdown: Record<string, RecommendationBreakdown> = {};
  for (const entry of topEntries) {
    breakdown[entry.item.id] = {
      score: Number(entry.score.toFixed(2)),
      reasons: Array.from(entry.reasons),
    };
  }

  let source: RecommendationSource = 'personalized';
  if (!hasTasteSignals && hasHistorySignals) {
    source = 'history';
  }
  if (!hasTasteSignals && !hasHistorySignals) {
    source = hasEngagementSignals ? 'signals' : 'popular';
  }

  return {
    items: topEntries.map((entry) => entry.item),
    source,
    breakdown,
    consentPersonalization: effectiveConsent,
    tasteSnapshot: hasTasteSignals ? tasteSnapshot : null,
    holdout,
    modelVersion: resolvedModelVersion,
    guestProfileId,
  };
}

export const __recInternals = {
  normalizeCategoryId,
  enforceCategoryDiversityRules,
};
